import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Input, Space, Modal, Form, Switch, InputNumber, message, Popconfirm, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, LockOutlined, UnlockOutlined } from '@ant-design/icons';
import { Get, Post, Put, DeleteSingle as Del } from '../../utils/httpProvider';
import { HOC_VI_API } from '../../utils/apiUrl';

const { Search } = Input;

const HocViList = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalItems, setTotalItems] = useState(0);
    const [pageIndex, setPageIndex] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [keyword, setKeyword] = useState('');

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [form] = Form.useForm();
    const [editingId, setEditingId] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        loadData();
    }, [pageIndex, pageSize, keyword]);

    const loadData = async () => {
        setLoading(true);
        try {
            const res = await Get(HOC_VI_API.GET_PAGING, null, {
                PageIndex: pageIndex,
                PageSize: pageSize,
                Keyword: keyword
            });
            if (res && res.data) {
                setData(res.data);
                setTotalItems(res.totalRecords || 0);
            }
        } catch (error) {
            message.error('Lỗi khi tải dữ liệu');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (value) => {
        setKeyword(value);
        setPageIndex(1);
    };

    const showModal = (mode, record = null) => {
        setModalMode(mode);
        setIsModalVisible(true);
        if (mode === 'edit' && record) {
            setEditingId(record.id);
            form.setFieldsValue({
                tenHocVi: record.tenHocVi,
                soThuTu: record.soThuTu,
                isActive: record.isActive
            });
        } else {
            setEditingId(null);
            form.resetFields();
            form.setFieldsValue({ isActive: 1, soThuTu: data.length + 1 });
        }
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setSubmitting(true);

            // Kiểm tra trùng tên
            const checkRes = await Get(HOC_VI_API.GET_PAGING, null, { PageIndex: 1, PageSize: 1000 });
            if (checkRes && checkRes.data) {
                const nameLower = values.tenHocVi.trim().toLowerCase();
                const isDuplicate = checkRes.data.some(item => 
                    item.tenHocVi && item.tenHocVi.trim().toLowerCase() === nameLower && 
                    (modalMode === 'add' || item.id !== editingId)
                );
                if (isDuplicate) {
                    message.error('Tên học vị này đã tồn tại trong danh mục!');
                    setSubmitting(false);
                    return;
                }
            }

            const currentRecord = data.find(item => item.id === editingId);
            const currentActive = currentRecord ? (currentRecord.isActive === true || currentRecord.isActive === 1 || String(currentRecord.isActive).trim().toLowerCase() === 'true') : true;

            const payload = { 
                ...values,
                isActive: modalMode === 'add' ? 1 : (currentActive ? 1 : 0)
            };

            if (modalMode === 'add') {
                await Post(HOC_VI_API.ADD_ITEM, null, payload);
                message.success('Thêm mới thành công');
            } else {
                const editUrl = HOC_VI_API.EDIT_ITEM.includes('{id}') 
                    ? HOC_VI_API.EDIT_ITEM.replace('{id}', editingId) 
                    : `${HOC_VI_API.EDIT_ITEM}/${editingId}`;
                await Put(editUrl, null, payload);
                message.success('Cập nhật thành công');
            }
            setIsModalVisible(false);
            loadData();
        } catch (error) {
            console.error(error);
            const errorMsg = error && typeof error === 'object' && error.message ? error.message : (typeof error === 'string' ? error : 'Có lỗi xảy ra');
            message.error(errorMsg);
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggleStatus = async (record) => {
        try {
            const currentActive = record.isActive === true || record.isActive === 1 || String(record.isActive).trim().toLowerCase() === 'true';
            const payload = { ...record, isActive: currentActive ? 0 : 1 };
            const editUrl = HOC_VI_API.EDIT_ITEM.includes('{id}') 
                ? HOC_VI_API.EDIT_ITEM.replace('{id}', record.id) 
                : `${HOC_VI_API.EDIT_ITEM}/${record.id}`;
            await Put(editUrl, null, payload);
            message.success('Cập nhật trạng thái thành công');
            loadData();
        } catch (error) {
            message.error('Cập nhật trạng thái thất bại');
        }
    };

    const handleDelete = async (id) => {
        try {
            const deleteUrl = HOC_VI_API.DELETE_ITEM.includes('{id}') 
                ? HOC_VI_API.DELETE_ITEM.replace('{id}', id) 
                : `${HOC_VI_API.DELETE_ITEM}/${id}`;
            await Del(deleteUrl);
            message.success('Xóa thành công');
            loadData();
        } catch (error) {
            message.error('Xóa thất bại');
        }
    };

    const columns = [
        {
            title: 'Tên học vị',
            dataIndex: 'tenHocVi',
            key: 'tenHocVi',
        },
        {
            title: 'Số thứ tự',
            dataIndex: 'soThuTu',
            key: 'soThuTu',
            width: 120,
            align: 'center'
        },
        {
            title: 'Trạng thái',
            dataIndex: 'isActive',
            key: 'isActive',
            render: (isActive) => (
                <Tag color={isActive ? 'success' : 'default'}>
                    {isActive ? 'Hoạt động' : 'Tạm khóa'}
                </Tag>
            )
        },
        {
            title: 'Thao tác',
            key: 'action',
            render: (_, record) => (
                <Space size="middle">
                    <Popconfirm
                        title={`Bạn có chắc chắn muốn ${record.isActive ? 'khóa' : 'mở khóa'}?`}
                        onConfirm={() => handleToggleStatus(record)}
                        okText="Có"
                        cancelText="Không"
                    >
                        <Button type="link" icon={record.isActive ? <LockOutlined /> : <UnlockOutlined />}>
                            {record.isActive ? 'Khóa' : 'Mở khóa'}
                        </Button>
                    </Popconfirm>
                    <Button type="link" icon={<EditOutlined />} onClick={() => showModal('edit', record)}>
                        Sửa
                    </Button>
                    <Popconfirm
                        title="Bạn có chắc chắn muốn xóa?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Có"
                        cancelText="Không"
                    >
                        <Button type="link" danger icon={<DeleteOutlined />}>
                            Xóa
                        </Button>
                    </Popconfirm>
                </Space>
            ),
            width: 250
        }
    ];

    return (
        <Card title="Danh mục Học vị" extra={
            <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal('add')}>
                Thêm mới
            </Button>
        }>
            <div style={{ marginBottom: 16 }}>
                <Search
                    placeholder="Tìm kiếm..."
                    allowClear
                    onSearch={handleSearch}
                    style={{ width: 300 }}
                />
            </div>

            <Table
                columns={columns}
                dataSource={data}
                rowKey="id"
                loading={loading}
                scroll={{ x: 'max-content' }}
                pagination={{
                    current: pageIndex,
                    pageSize: pageSize,
                    total: totalItems,
                    onChange: (page, size) => {
                        setPageIndex(page);
                        setPageSize(size);
                    }
                }}
            />

            <Modal
                title={modalMode === 'add' ? 'Thêm mới học vị' : 'Cập nhật học vị'}
                open={isModalVisible}
                onOk={handleSubmit}
                onCancel={handleCancel}
                confirmLoading={submitting}
                okText="Lưu"
                cancelText="Hủy"
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="tenHocVi"
                        label="Tên học vị"
                        rules={[{ required: true, message: 'Vui lòng nhập tên học vị' }]}
                    >
                        <Input placeholder="Nhập tên học vị" />
                    </Form.Item>
                    <Form.Item
                        name="soThuTu"
                        label="Số thứ tự"
                        rules={[{ required: true, message: 'Vui lòng nhập số thứ tự' }]}
                    >
                        <InputNumber style={{ width: '100%' }} min={1} />
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default HocViList;
